import React, { useContext, useState } from 'react';
import { Button } from '@amzn/awsui-components-core';
import { ContentEngagement, EngagementSource } from '@amzn/awscat-pattern-library-service-model';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { AppContext } from '../../contexts/AppContext';
import ContentEngagementModal from '../content-engagement/ContentEngagementModal';
interface Props {
  buttonText?: string;
  buttonVariant: 'normal' | 'icon';
  children?: React.ReactNode;
  contentId: string;
  iconUrl: string;
  onFetch?: (fetchedEngagements: ContentEngagement[]) => Promise<void>;
  onUpdate?: (updatedEngagements: ContentEngagement[]) => void;
  isCollection?: boolean;
}

const ContentEngagementButton = ({
  buttonText,
  buttonVariant,
  children,
  contentId,
  onFetch,
  onUpdate,
  iconUrl,
  isCollection = false,
}: Props) => {
  const [engagements, setEngagements] = useState([]);
  const [hasLoaded, setHasLoaded] = useState<boolean>(true);
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const appContext = useContext(AppContext);
  // @ts-expect-error TS18048 - strict migration
  const { userId } = appContext.authnInfo.userProfile;
  const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();

  const fetchEngagements = async () => {
    setHasLoaded(false);
    const engagementsResponse = await mplServiceClient.getContentEngagements(contentId, userId);
    // @ts-expect-error TS2345 - strict migration
    setEngagements(engagementsResponse.contentEngagements);
    if (onFetch) {
      await onFetch(engagementsResponse.contentEngagements);
    }
    setHasLoaded(true);
  };

  const updateEngagements = async (updatedEngagements: ContentEngagement[]) => {
    setHasLoaded(false);
    const engagementSource = isCollection ? EngagementSource.Collection : EngagementSource.Guidance;
    if (engagements.length > 0) {
      await mplServiceClient.updateContentEngagements(
        contentId,
        userId,
        updatedEngagements,
        engagementSource,
      );
    } else {
      await mplServiceClient.createContentEngagements(
        contentId,
        userId,
        updatedEngagements,
        engagementSource,
      );
    }
    if (onUpdate) {
      onUpdate(updatedEngagements);
    }
    setHasLoaded(true);
    setModalVisible(false);
  };

  const showModal = async () => {
    await fetchEngagements();
    setModalVisible(true);
  };

  const dismissModal = () => {
    setModalVisible(false);
  };

  const engagementModal = (
    <ContentEngagementModal
      engagements={engagements}
      onUpdate={updateEngagements}
      onCancel={dismissModal}
      visible={modalVisible}
      loading={!hasLoaded}
    />
  );

  return (
    <>
      <Button
        iconUrl={iconUrl}
        iconAlt={buttonText || 'Content engagement'}
        variant={buttonVariant}
        onClick={showModal}
        loading={!hasLoaded}
      >
        {buttonText}
      </Button>
      {children}
      {engagementModal}
    </>
  );
};

export default ContentEngagementButton;
